import React, { useEffect, useState } from 'react';
import { Save } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, IconButton, Snackbar, Typography } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { UrlApi } from '../../../services/apirest';

// Convierte un arreglo plano en una estructura tipo árbol
function buildMenuTree(flatMenus) {
    const menuMap = {};
    const tree = [];

    flatMenus.forEach(menu => {
        menuMap[menu.id] = { ...menu, children: [] };
    });

    flatMenus.forEach(menu => {
        if (menu.menu_padre_id && menuMap[menu.menu_padre_id]) {
            menuMap[menu.menu_padre_id].children.push(menuMap[menu.id]);
        } else {
            tree.push(menuMap[menu.id]);
        }
    });

    return tree;
}

// Renderiza recursivamente el árbol de menús
const RenderMenuTree = ({ treeData, updateItem }) => (
    <ul style={{ listStyle: 'none', paddingLeft: '10px', margin: 0 }}>
        {treeData.map(menu => (
            <li key={menu.id} style={{ marginBottom: '5px' }}>
                {menu.children && menu.children.length > 0 ? (
                    <Typography
                        variant="subtitle1"
                        sx={{
                            backgroundColor: 'black',
                            color: 'white',
                            padding: '5px',
                            borderRadius: '3px',
                            fontWeight: 'bold',
                            width: '100%'
                        }}
                    >
                        {menu.menu}
                    </Typography>
                ) : (
                    <FormControlLabel
                        control={
                            <Checkbox
                                value={menu.id}
                                checked={menu.permitido === 1}
                                onChange={e => updateItem(e, menu.id)}
                            />
                        }
                        label={menu.menu}
                    />
                )}
                {menu.children && menu.children.length > 0 && (
                    <RenderMenuTree treeData={menu.children} updateItem={updateItem} />
                )}
            </li>
        ))}
    </ul>
);

export default function GestionMenues(props) {
    const [editado, setEditado] = useState(false);
    const [menus, setMenus] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    useEffect(() => {
        obtenerMenu();
    }, []);

    const obtenerMenu = async () => {
        try {
            const response = await fetch(UrlApi + "/menues/todos/" + props.wusername);
            const menuData = await response.json();
            setMenus(menuData);
        } catch (error) {
            mostrarSnackbar('Error al obtener menús', 'error');
            console.error('Error al obtener menús:', error);
        }
    };

    // Gestiona la selección de un menú y propaga a ancestros
    const updateItem = (event, id) => {
        const clone = [...menus];
        const idx = clone.findIndex(m => m.id === id);
        if (idx === -1) return;
        // Actualiza ítem seleccionado
        clone[idx] = {
            ...clone[idx],
            permitido: event.target.checked ? 1 : 0,
            username: props.wusername,
            id_menu: clone[idx].idmenu,
            usuario_registra: localStorage.getItem('user'),
            favorito: clone[idx].favorito ?? 0
        };

        // Propaga hacia arriba si se marcó
        if (event.target.checked) {
            let pid = clone[idx].menu_padre_id;
            while (pid) {
                const pIdx = clone.findIndex(m => m.id === pid);
                if (pIdx === -1) break;
                clone[pIdx] = {
                    ...clone[pIdx],
                    permitido: 1,
                    username: props.wusername,
                    id_menu: clone[pIdx].idmenu,
                    usuario_registra: localStorage.getItem('user'),
                    favorito: clone[pIdx].favorito ?? 0
                };
                pid = clone[pIdx].menu_padre_id;
            }
        } else {
            // Si se desmarcó, desmarca ancestros que no tengan hijos marcados
            let pid = clone[idx].menu_padre_id;
            while (pid) {
                const pIdx = clone.findIndex(m => m.id === pid);
                if (pIdx === -1) break;
                const hasChild = clone.some(m => m.menu_padre_id === pid && m.permitido === 1);
                if (!hasChild) {
                    clone[pIdx] = {
                        ...clone[pIdx],
                        permitido: 0,
                        username: props.wusername,
                        id_menu: clone[pIdx].idmenu,
                        usuario_registra: localStorage.getItem('user'),
                        favorito: clone[pIdx].favorito ?? 0
                    };
                }
                pid = clone[pIdx].menu_padre_id;
            }
        }

        setMenus(clone);
        setEditado(true);
    };

    // Guarda menús incluyendo ancestros marcados
    const grabarMenues = async e => {
        e.preventDefault();
        const clone = [...menus];
        // Asegura que todos los ancestros de ítems marcados estén marcados
        clone.forEach((m, i) => {
            if (m.permitido === 1) {
                let pid = m.menu_padre_id;
                while (pid) {
                    const pIdx = clone.findIndex(x => x.id === pid);
                    if (pIdx === -1) break;
                    clone[pIdx] = {
                        ...clone[pIdx],
                        permitido: 1,
                        username: props.wusername,
                        id_menu: clone[pIdx].idmenu,
                        usuario_registra: localStorage.getItem('user'),
                        favorito: clone[pIdx].favorito ?? 0
                    };
                    pid = clone[pIdx].menu_padre_id;
                }
            }
        });
        const menusFiltrados = clone.filter(m => m.permitido === 1);

        try {
            const del = await fetch(UrlApi + "/menues/" + props.wusername, { method: 'DELETE' });
            if (!del.ok) throw new Error(del.statusText);
            const post = await fetch(UrlApi + "/menues", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(menusFiltrados)
            });
            if (!post.ok) throw new Error(post.statusText);
            mostrarSnackbar('Se guardaron los menús seleccionados', 'success');
            setEditado(false);
        } catch (err) {
            mostrarSnackbar('¡Ups! Algo salió mal', 'error');
            console.error('¡Ups! Algo salió mal:', err);
        }
    };

    const mostrarSnackbar = (msg, sev) => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(sev);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const treeMenus = buildMenuTree(menus);

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: '55px',
                    zIndex: 2,
                    backgroundColor: 'white',
                    border: '1px solid violet',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
                    height: 40,
                    margin: '15px'
                }}
            >
                <Typography variant="h6" ml={1}>
                    Menúes habilitados
                </Typography>
                {editado && (
                    <IconButton onClick={grabarMenues}>
                        <Save color="success" />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', pt: '30px', pl: 1, pr: 1 }}>
                <RenderMenuTree treeData={treeMenus} updateItem={updateItem} />
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{ sx: { zIndex: 3000 } }}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </div>
    );
}
